'use client';

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

/**
 * Renders the video iframe for the VideoWayfinder component with specific
 * styling for portrait videos.
 *
 * Note: This is highly coupled to the VideoWayfinder and is not a reusable
 * component. (Rename if needed to create a new generic VideoEmbed component.)
 */
const VideoEmbed = ({ url, portraitVideo, type, title }) => {
  const containerRef = useRef(null);
  const [shouldRenderIframe, setShouldRenderIframe] = useState(false);
  const needsSpecialStyles = !portraitVideo || type === 'youtube';

  useEffect(() => {
    if (shouldRenderIframe) return;

    // If already scrolled on mount, trigger immediately
    if (typeof window !== 'undefined' && window.scrollY > 0) {
      setShouldRenderIframe(true);
      return;
    }

    const onPageScroll = () => {
      if (window.scrollY <= 0) return;
      setShouldRenderIframe(true);
      window.removeEventListener('scroll', onPageScroll);
    };

    window.addEventListener('scroll', onPageScroll, { passive: true });

    return () => window.removeEventListener('scroll', onPageScroll);
  }, [shouldRenderIframe]);

  return (
    <div
      ref={containerRef}
      className={clsx(`m-auto flex justify-center`, {
        'w-full sm:-top-20 lg:top-0 lg:bottom-0 lg:w-[600px]':
          needsSpecialStyles
      })}
    >
      {shouldRenderIframe ? (
        <iframe
          title={title}
          className={clsx(
            'w-full',
            portraitVideo && type === 'vimeo'
              ? 'h-[384px] sm:h-[512px]'
              : 'h-[240px] sm:h-[300px]'
          )}
          src={url}
          loading="lazy"
          fetchPriority="low"
          width="100%"
          height="100%"
          allow="autoplay; fullscreen"
          frameBorder="0"
          allowFullScreen={true}
        ></iframe>
      ) : (
        <div
          aria-hidden="true"
          className={clsx(
            'w-full bg-neutral-100',
            portraitVideo && type === 'vimeo'
              ? 'h-[384px] sm:h-[512px]'
              : 'h-[240px] sm:h-[300px]'
          )}
        />
      )}
    </div>
  );
};

export default VideoEmbed;
